import {
  fetchGetProductOffers,
  fetchPostApplicationDecline,
  fetchPostFormalOffers,
} from '@iwoca/lapi-client/edge';

import {
  checkStatusIsDeclined,
  getApprovalRequirementErrors,
  getIwocaPayProductOffers,
  getProductDeclineUid,
  getSuggestedProduct,
} from '../Dashboard.helpers';

type ProcessResult = 'formalised' | 'declined' | 'blocked' | 'noop';

/**
 * Processes an open iwocaPay application: fetches product offers, handles
 * declines, and formalises offers.
 *
 * Shared between useSuspenseApplicationProcessor (dashboard) and
 * useAwaitPostCheckout (post-checkout guard) so that formalisation can
 * happen before the dashboard renders.
 */
export async function processOpenApplication(
  stateKey: string,
): Promise<ProcessResult> {
  const productOffersResponse = await fetchGetProductOffers({ stateKey });
  const productOffers = productOffersResponse.data;

  if (!productOffers) return 'noop';

  const suggestedProduct = getSuggestedProduct(productOffers);

  if (!suggestedProduct) return 'noop';

  const approvalRequirementErrors =
    getApprovalRequirementErrors(suggestedProduct);
  if (Array.from(approvalRequirementErrors.values()).length) {
    return 'blocked';
  }

  const isDeclined = checkStatusIsDeclined(suggestedProduct);

  if (isDeclined) {
    const productDeclineUid = getProductDeclineUid(suggestedProduct);
    const declineStatus =
      suggestedProduct.status === 'hard_declined'
        ? 'Hard Declined'
        : 'Soft Declined';

    await fetchPostApplicationDecline({
      stateKey,
      body: {
        data: {
          status: declineStatus,
          product_type_decline_uid: productDeclineUid!,
        },
      },
    });

    return 'declined';
  }

  const iwocaPayProductOffers = getIwocaPayProductOffers(productOffers);

  if (iwocaPayProductOffers && iwocaPayProductOffers.length > 0) {
    await fetchPostFormalOffers({
      stateKey,
      body: {
        data: {
          offer_ids: iwocaPayProductOffers.map((offer) => offer.id),
        },
      },
    });

    return 'formalised';
  }

  return 'noop';
}
